import React, { useState } from 'react';
import AppLayout from '../components/AppLayout';
import { AlertCircle, Bug, Lightbulb, Upload, CheckCircle, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import VideoHelpButton from '../components/VideoHelpButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SupportRequestForm = () => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    request_type: 'bug',
    title: '',
    description: '',
    severity: 'medium',
    steps_to_reproduce: '',
    expected_behavior: '',
    actual_behavior: ''
  });
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const severityOptions = [
    { value: 'critical', label: 'Critical', description: 'System down or blocking all work', color: 'text-red-500' },
    { value: 'high', label: 'High', description: 'Major functionality broken', color: 'text-orange-500' },
    { value: 'medium', label: 'Medium', description: 'Minor issue or nice-to-have', color: 'text-yellow-500' },
    { value: 'low', label: 'Low', description: 'Cosmetic or suggestion', color: 'text-green-500' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setError('Only image files (JPEG, PNG, WebP, GIF) and PDF are supported');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setAttachment(file);
      setError(null);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/support-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit request');
      }

      const result = await response.json();

      if (attachment) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', attachment);
        
        await fetch(`${BACKEND_URL}/api/support-requests/${result.request_id}/attachment`, {
          method: 'POST',
          credentials: 'include',
          body: formDataUpload
        });
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      request_type: 'bug',
      title: '',
      description: '',
      severity: 'medium',
      steps_to_reproduce: '',
      expected_behavior: '',
      actual_behavior: ''
    });
    setAttachment(null);
    setSubmitted(false);
    setError(null);
  };

  const inputClass = `w-full ${isDark ? 'bg-black border-white/20 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border rounded-sm px-4 py-2 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] outline-none`;
  const textareaClass = `w-full ${isDark ? 'bg-black border-white/20 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border rounded-sm px-4 py-2 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] outline-none resize-none`;
  const boxClass = `${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm`;

  if (submitted) {
    return (
      <AppLayout title="Bugs & Requests">
        <div className="max-w-2xl mx-auto">
          <div className={`${isDark ? 'bg-[#0A0A0A] border-[#A2FF00]/30' : 'bg-white border-green-300'} border rounded-sm p-8 text-center`}>
            <CheckCircle className={`w-16 h-16 ${isDark ? 'text-[#A2FF00]' : 'text-green-500'} mx-auto mb-4`} />
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>Request Submitted Successfully!</h2>
            <p className="text-gray-400 mb-6">
              Thank you for your feedback. Our team will review your request and get back to you if needed.
            </p>
            <button
              onClick={resetForm}
              className={`px-6 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-medium rounded-sm transition-colors`}
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Bugs & Requests">
      <div className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Request Type Selection */}
          <div className={`${boxClass} p-6`}>
            <label className={`block ${isDark ? 'text-white' : 'text-gray-900'} font-medium mb-4`}>What would you like to submit?</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, request_type: 'bug' }))}
                className={`p-4 rounded-sm border-2 transition-all ${
                  formData.request_type === 'bug'
                    ? `border-[#A2FF00] ${isDark ? 'bg-[#A2FF00]/10' : 'bg-green-50'}`
                    : `${isDark ? 'border-white/10 hover:border-white/30' : 'border-gray-200 hover:border-gray-400'}`
                }`}
              >
                <Bug className={`w-8 h-8 mx-auto mb-2 ${formData.request_type === 'bug' ? (isDark ? 'text-[#A2FF00]' : 'text-green-600') : 'text-gray-400'}`} />
                <span className={`font-medium ${formData.request_type === 'bug' ? (isDark ? 'text-[#A2FF00]' : 'text-green-600') : (isDark ? 'text-white' : 'text-gray-900')}`}>
                  Bug Report
                </span>
                <p className="text-xs text-gray-500 mt-1">Report an issue or problem</p>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, request_type: 'feature' }))}
                className={`p-4 rounded-sm border-2 transition-all ${
                  formData.request_type === 'feature'
                    ? `border-[#A2FF00] ${isDark ? 'bg-[#A2FF00]/10' : 'bg-green-50'}`
                    : `${isDark ? 'border-white/10 hover:border-white/30' : 'border-gray-200 hover:border-gray-400'}`
                }`}
              >
                <Lightbulb className={`w-8 h-8 mx-auto mb-2 ${formData.request_type === 'feature' ? (isDark ? 'text-[#A2FF00]' : 'text-green-600') : 'text-gray-400'}`} />
                <span className={`font-medium ${formData.request_type === 'feature' ? (isDark ? 'text-[#A2FF00]' : 'text-green-600') : (isDark ? 'text-white' : 'text-gray-900')}`}>
                  Feature Request
                </span>
                <p className="text-xs text-gray-500 mt-1">Suggest a new feature</p>
              </button>
            </div>
          </div>

          {/* Main Form Fields */}
          <div className={`${boxClass} p-6 space-y-4`}>
            {/* Title */}
            <div>
              <label className={`block ${isDark ? 'text-white' : 'text-gray-900'} font-medium mb-2`}>
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder={formData.request_type === 'bug' ? 'Brief summary of the issue' : 'Brief summary of your idea'}
                className={inputClass}
              />
            </div>

            {/* Description */}
            <div>
              <label className={`block ${isDark ? 'text-white' : 'text-gray-900'} font-medium mb-2`}>
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={4}
                placeholder={formData.request_type === 'bug' ? 'Detailed description of what happened' : 'Describe the feature you would like to see'}
                className={textareaClass}
              />
            </div>

            {/* Severity */}
            <div>
              <label className={`block ${isDark ? 'text-white' : 'text-gray-900'} font-medium mb-2`}>
                Priority / Severity <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {severityOptions.map(option => (
                  <label
                    key={option.value}
                    className={`flex items-center p-3 rounded-sm border cursor-pointer transition-all ${
                      formData.severity === option.value
                        ? `border-[#A2FF00] ${isDark ? 'bg-[#A2FF00]/5' : 'bg-green-50'}`
                        : `${isDark ? 'border-white/10 hover:border-white/30' : 'border-gray-200 hover:border-gray-400'}`
                    }`}
                  >
                    <input
                      type="radio"
                      name="severity"
                      value={option.value}
                      checked={formData.severity === option.value}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-3 h-3 rounded-full mr-3 ${
                      option.value === 'critical' ? 'bg-red-500' :
                      option.value === 'high' ? 'bg-orange-500' :
                      option.value === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                    }`} />
                    <div className="flex-1">
                      <span className={`font-medium ${option.color}`}>{option.label}</span>
                      <span className="text-gray-500 text-sm ml-2">- {option.description}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Bug-specific Fields */}
          {formData.request_type === 'bug' && (
            <div className={`${boxClass} p-6 space-y-4`}>
              <h3 className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium mb-2 flex items-center gap-2`}>
                <AlertCircle className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                Additional Details (Optional but helpful)
              </h3>
              
              <div>
                <label className="block text-gray-400 text-sm mb-2">Steps to Reproduce</label>
                <textarea
                  name="steps_to_reproduce"
                  value={formData.steps_to_reproduce}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                  className={textareaClass}
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Expected Behavior</label>
                <textarea
                  name="expected_behavior"
                  value={formData.expected_behavior}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="What you expected to happen"
                  className={textareaClass}
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Actual Behavior</label>
                <textarea
                  name="actual_behavior"
                  value={formData.actual_behavior}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="What actually happened"
                  className={textareaClass}
                />
              </div>
            </div>
          )}

          {/* Attachment */}
          <div className={`${boxClass} p-6`}>
            <label className={`block ${isDark ? 'text-white' : 'text-gray-900'} font-medium mb-2`}>
              Attachment (Optional)
            </label>
            <p className="text-gray-500 text-sm mb-3">Upload a screenshot or file to help explain the issue</p>
            
            {!attachment ? (
              <label className={`flex items-center justify-center gap-2 p-6 border-2 border-dashed ${isDark ? 'border-white/20 hover:border-[#A2FF00]/50' : 'border-gray-300 hover:border-green-400'} rounded-sm cursor-pointer transition-colors`}>
                <Upload className="w-5 h-5 text-gray-400" />
                <span className="text-gray-400">Click to upload (JPEG, PNG, WebP, GIF, PDF - Max 10MB)</span>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".jpg,.jpeg,.png,.webp,.gif,.pdf"
                  className="hidden"
                />
              </label>
            ) : (
              <div className={`flex items-center justify-between p-4 ${isDark ? 'bg-[#A2FF00]/10 border-[#A2FF00]/30' : 'bg-green-50 border-green-200'} border rounded-sm`}>
                <span className={`${isDark ? 'text-[#A2FF00]' : 'text-green-700'}`}>{attachment.name}</span>
                <button
                  type="button"
                  onClick={removeAttachment}
                  className="text-gray-400 hover:text-red-400 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 text-red-400">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting || !formData.title || !formData.description}
              className={`px-8 py-3 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-medium rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
};

export default SupportRequestForm;
